'use client';

import React, { useMemo } from 'react';
import { Button } from 'src/components/PulseUI/Button';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { useGetProviderCohortsQuery, useGetProviderCohortByIdQuery } from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders, faFileInvoice } from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import {
  useAppSelector,
  selectCurrentTableType,
  selectAllAppliedColumnSettings,
} from 'src/redux';
import EnrichingCodesInfo from '../PulseUI/EnrichingCodesInfo/EnrichingCodesInfo';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { exists } from 'src/utils/exists/exists';

/**
 * Props interface for the EncountersHeaderVolume component
 */
interface EncountersHeaderVolumeProps {
  /** Field identifier for the column */
  field?: string;
  /** Table type identifier */
  tableType?: string;
  /** Additional props, including colDef from ag-grid */
  colDef?: {
    field?: string;
    headerName?: string;
  };
  [key: string]: any;
}

/**
 * Header component for volume/billing data in encounters table
 * Displays a button that opens the volume filter drawer when clicked
 */
const EncountersHeaderVolume: React.FC<EncountersHeaderVolumeProps> = ({
  field,
  tableType,
  colDef,
  ...props
}) => {
  const { dataCoverage: dataCoverageFlag } = useFlags();
  const { closeAllButCurrent } = useDrawer();
  const { setColumnId } = useTableFilterSelections(tableType);

  const { data: allCodeSets } = useGetFilterSetsQuery({
    type: 'CODE',
  });

  // Add this to get provider cohorts
  const { data: providerCohorts } = useGetProviderCohortsQuery();

  const id = field?.split('_')[0] || colDef?.field?.split('_')[0];

  // Check if this ID belongs to a cohort before calling the hook
  const isCohort = useMemo(() => {
    if (!id || !providerCohorts?.results) return false;
    return providerCohorts.results.some(
      (cohort) => cohort.id.toString() === id
    );
  }, [id, providerCohorts?.results]);

  // Only call useGetProviderCohortByIdQuery if this is actually a cohort
  const { data: selectedCohort } = useGetProviderCohortByIdQuery(
    isCohort ? id : null,
    { skip: !isCohort || !id }
  );

  const currentCodeSet = useMemo(() => {
    if (!allCodeSets?.results) return [];

    return allCodeSets?.results?.find(
      (codeSet) =>
        codeSet.id.toString() === field?.split('_')[0] ||
        codeSet.id.toString() === colDef?.field?.split('_')[0]
    );
  }, [allCodeSets?.results, field, colDef?.field]);

  const dataCoverage = useMemo(() => {
    if (!currentCodeSet || Array.isArray(currentCodeSet)) return undefined;
    return currentCodeSet?.detailed_filters?.flatMap(
      (filter: any) =>
        filter.detailed_values?.map((value: any) => value.vrdc_enriched) ?? []
    );
  }, [currentCodeSet]);

  // Get the current table type from Redux
  const currentTableType = useAppSelector(selectCurrentTableType);

  // Use the provided table type or fall back to the current table type from Redux
  const activeTableType = tableType || currentTableType;

  /**
   * Handle button click to open the volume filter drawer
   * Sets the column ID and opens the drawer
   */

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setColumnId(field, undefined, true); // isEditing = true for existing columns
    closeAllButCurrent(`table-volume-${activeTableType}`);
  };

  // Get the column settings from Redux for the current field
  const allAppliedColumnSettings = useAppSelector((state) =>
    selectAllAppliedColumnSettings(state, activeTableType)
  );

  // Calculate filter count based on presence of place of service and payer type filters
  const filterCount = useMemo(() => {
    // If no field is provided, return 0
    if (!field) return 0;

    // Get the column settings for the current field
    const columnSettings = allAppliedColumnSettings[field];
    if (!columnSettings) return 0;

    // Check if place of service filter is applied
    const hasPlaceOfService =
      columnSettings.placeOfServiceFilter?.values &&
      columnSettings.placeOfServiceFilter.values.length > 0;

    // Check if payer type filter is applied
    const hasPayerType =
      columnSettings.payerTypeFilter?.values &&
      columnSettings.payerTypeFilter.values.length > 0;

    // Return count based on which filters are present
    if (hasPlaceOfService && hasPayerType) return 2;
    if (hasPlaceOfService || hasPayerType) return 1;
    return 0;
  }, [field, allAppliedColumnSettings]);

  // Determine the badge styling based on filter count
  const badgeClassName =
    filterCount === 0 ? 'bg-teal-050 text-primary' : 'bg-teal-600 text-white';

  return (
    <div className="flex items-center justify-center w-full h-full">
      <Button
        size="sm"
        variant="outlined"
        className="w-full rounded-full bg-white border-teal-400 text-primary px-3"
        onClick={handleClick}
        startDecorator={
          <Icon icon={faFileInvoice} className="text-neutral-600" />
        }
        endDecorator={
          <div
            className={`flex items-center justify-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClassName}`}
          >
            <Icon icon={faSliders} /> {filterCount}
          </div>
        }
      >
        <div className="flex items-center gap-1 min-w-0 max-w-full">
          <div className="truncate">
            {colDef?.headerName || props?.colDef?.headerName}
          </div>
          {dataCoverageFlag && (
            <EnrichingCodesInfo
              dataCoverage={dataCoverage}
              iconOnly={true}
              isEncounter={exists(selectedCohort)}
            />
          )}
        </div>
      </Button>
    </div>
  );
};

export default EncountersHeaderVolume;
